import { Injectable } from '@angular/core';
import { SerializedE2eeKeyStore } from './e2ee-key-store';

const DB_NAME = 'meplis-e2ee';
const DB_VERSION = 1;
const ENTRIES = 'entries';
const KEYS = 'keys';
const WRAPPING_KEY_ID = 'wrapping-key';

interface EncryptedEntry {
  iv: Uint8Array<ArrayBuffer>;
  data: ArrayBuffer;
}

/**
 * Entries are encrypted with an AES-GCM key generated as non-extractable and
 * kept in IndexedDB itself: a copy of the database files alone does not reveal
 * the private keys. Only call from the browser (never during SSR).
 */
@Injectable()
export class E2eeKeyStoreWeb extends SerializedE2eeKeyStore {
  private _db: Promise<IDBDatabase> | null = null;
  private _wrappingKey: Promise<CryptoKey> | null = null;

  protected async readEntry(key: string): Promise<string | null> {
    const entry = await this._request<EncryptedEntry | undefined>(ENTRIES, 'readonly', (store) =>
      store.get(key)
    );
    if (!entry) {
      return null;
    }

    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: entry.iv, additionalData: new TextEncoder().encode(key) },
      await this._getWrappingKey(),
      entry.data
    );
    return new TextDecoder().decode(plaintext);
  }

  protected async writeEntry(key: string, value: string): Promise<void> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(key) },
      await this._getWrappingKey(),
      new TextEncoder().encode(value)
    );
    await this._request(ENTRIES, 'readwrite', (store) => store.put({ iv, data }, key));
  }

  protected async deleteEntry(key: string): Promise<void> {
    await this._request(ENTRIES, 'readwrite', (store) => store.delete(key));
  }

  private _getWrappingKey(): Promise<CryptoKey> {
    this._wrappingKey ??= (async () => {
      const existing = await this._request<CryptoKey | undefined>(KEYS, 'readonly', (store) =>
        store.get(WRAPPING_KEY_ID)
      );
      if (existing) {
        return existing;
      }

      const created = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
        'decrypt',
      ]);
      try {
        // `add`, not `put`: if another tab created one meanwhile, keep that
        // one -- entries it already wrote are only readable with it.
        await this._request(KEYS, 'readwrite', (store) => store.add(created, WRAPPING_KEY_ID));
        return created;
      } catch {
        const winner = await this._request<CryptoKey | undefined>(KEYS, 'readonly', (store) =>
          store.get(WRAPPING_KEY_ID)
        );
        if (!winner) {
          throw new Error('Could not create the key store wrapping key');
        }
        return winner;
      }
    })().catch((error) => {
      this._wrappingKey = null;
      throw error;
    });

    return this._wrappingKey;
  }

  private _open(): Promise<IDBDatabase> {
    this._db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(ENTRIES);
        request.result.createObjectStore(KEYS);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }).catch((error) => {
      this._db = null;
      throw error;
    });

    return this._db;
  }

  private async _request<T>(
    storeName: string,
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest
  ): Promise<T> {
    const db = await this._open();

    return new Promise<T>((resolve, reject) => {
      const request = action(db.transaction(storeName, mode).objectStore(storeName));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => reject(request.error);
    });
  }
}
